import Level from "./Level"
import { AnimatedSprite, Container, Graphics, Point, Rectangle, Texture, Ticker } from "pixi.js"
import { SHOW_BOUNDING_BOXES, SHOW_DEBUG_GRAPHICS } from "./config"
import { ColorOverlayFilter } from "pixi-filters"
import Clock, { ClockCallbackObject } from "./Clock"
import Match from "./Match"

export type Direction = "Up" | "Down" | "Left" | "Right"
export type Animations = { [key in Direction]: Texture[] }
export enum CollisionType {
  BoundingBox,
  HitBox
}

export default class Entity extends AnimatedSprite {
  private _staticTileCoords: boolean
  private _tileCoords: { x: number, y: number } = this.getTileCoords()

  protected _alive = true
  get alive() {
    return this._alive
  }

  protected _speed: number
  get speed() {
    return this._speed
  }

  level: Level

  private _direction: Direction
  get direction() {
    return this._direction
  }

  private _moving: boolean
  get isMoving() {
    return this._moving
  }

  graphics: Graphics

  private _boundingBox: Rectangle
  private _boundingBoxPadding: Point
  private _hitBox: Rectangle | null = null
  private _hitBoxPadding: Point = new Point(0, 0)

  get x() {
    return this.position.x
  }
  set x(value) {
    this.position.x = value
    this._boundingBox.x = this.x + this._boundingBoxPadding.x
    if (this._hitBox) {
      this._hitBox.x = this.x + this._hitBoxPadding.x
    }
  }
  get y() {
    return this.position.y
  }
  set y(value) {
    this.position.y = value
    this._boundingBox.y = this.y + this._boundingBoxPadding.y
    if (this._hitBox) {
      this._hitBox.y = this.y + this._hitBoxPadding.y
    }
  }


  private _animations?: Animations
  private _animationTimeout: ClockCallbackObject | null = null
  private _previousDirection: Direction
  private _previousMovementAnimation: Texture[] | null = null
  private _tempDirection: Direction | null = null
  private _tempDestination: { x: number, y: number } | null = null

  private _colorOverlayFilter: ColorOverlayFilter = new ColorOverlayFilter({ alpha: 1 })
  private _blinkInterval: ClockCallbackObject | null = null

  // private _blin
  constructor(params: {
    x: number,
    y: number,
    width: number,
    height: number,
    speed: number,
    level: Level,
    textures: Texture[],
    animations?: Animations,
    staticTileCoords?: boolean,
  }) {
    super(params.textures)
    this.width = params.width
    this.height = params.height
    this._boundingBoxPadding = new Point(0, 0)
    this._boundingBox = new Rectangle(this.x + this._boundingBoxPadding.x, this.y + this._boundingBoxPadding.y, this.width, this.height)
    this._hitBox = null

    this.x = params.x
    this.y = params.y

    this._speed = params.speed
    this.level = params.level
    this.animationSpeed = 0.15
    this._direction = "Down"
    this._previousDirection = "Down"
    this._moving = false
    this._animations = params.animations
    this._staticTileCoords = params.staticTileCoords || false
    this.zIndex = this.y
    this.graphics = this.level.graphics

    this._colorOverlayFilter.enabled = false
    this.filters = [this._colorOverlayFilter]
  }

  startBlink(color: number) {
    if (this._blinkInterval) {
      this.stopBlink()
    }

    this._colorOverlayFilter.color = color
    this._colorOverlayFilter.enabled = true

    this._blinkInterval = Clock.setInterval(() => {
      this._colorOverlayFilter.color = color
      this._colorOverlayFilter.enabled = !this._colorOverlayFilter.enabled
    }, 100)
  }

  stopBlink() {
    if (!this._blinkInterval) return
    Clock.clear(this._blinkInterval)
    this._blinkInterval = null
    this._colorOverlayFilter.enabled = false
  }

  getHitBox(): Rectangle {
    if (this._hitBox) {
      return this._hitBox
    } else {
      return this.getBoundingBox()
    }
  }

  setHitBox(value: Rectangle) {
    this._hitBox = value
    this._hitBoxPadding = new Point(value.x, value.y)
    this._hitBox.x = this.x + this._hitBoxPadding.x
    this._hitBox.y = this.y + this._hitBoxPadding.y
  }

  getBoundingBox(): Rectangle {
    return this._boundingBox
  }

  setBoundingBox(value: Rectangle) {
    this._boundingBox = value
    this._boundingBoxPadding = new Point(value.x, value.y)
    this._boundingBox.x = this.x + this._boundingBoxPadding.x
    this._boundingBox.y = this.y + this._boundingBoxPadding.y
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isBlocking(_entityConstructor: typeof Entity.constructor | Entity): boolean {
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tickUpdate(_ticker: Ticker) {
    if (this.isMoving) {
      this.move()
    }
  }

  getTileCoords(position: { x: number, y: number } = { x: this.x, y: this.y }) {
    if (!position && this._staticTileCoords) {
      return this._tileCoords
    }
    return Level.getTileCoords(position)
  }

  setAtTileCoords(x = this.x, y = this.y) {
    const { x: centerX, y: centerY } = Level.getTileCoords({ x, y })
    this.x = centerX
    this.y = centerY
  }

  removeFromLevel(): void {
    this.level.removeChild(this)
  }

  stopMoving() {
    if (this._animationTimeout) {
      Clock.clear(this._animationTimeout)
      this._animationTimeout = null
    }
    this._moving = false
    this._previousMovementAnimation = null
    this._tempDirection = null
    this._tempDestination = null
  }

  startMoving(direction: Direction) {
    if (this.level instanceof Match && !this.level.started) return
    if (!this.alive) return
    this._moving = true
    this._previousDirection = this._direction
    this._direction = direction
    this._tempDirection = null
    this._tempDestination = null
  }

  getFutureCoords = (direction: Direction, speed: number) => {
    if (!this._moving) {
      return { x: this.x, y: this.y }
    }

    let futureX = this.x
    let futureY = this.y

    switch (direction) {
      case "Up":
        futureY -= speed
        break
      case "Down":
        futureY += speed
        break
      case "Left":
        futureX -= speed
        break
      case "Right":
        futureX += speed
        break
    }

    if (this._tempDestination) {
      // if the next step overrun the _tempDestination set the position to the _tempDestination and clear the _tempDestination
      switch (this._tempDirection) {
        case "Up":
          if (futureY <= this._tempDestination.y) {
            futureY = this._tempDestination.y
            this._tempDestination = null
            this._tempDirection = null
          }
          break
        case "Down":
          if (futureY >= this._tempDestination.y) {
            futureY = this._tempDestination.y
            this._tempDestination = null
            this._tempDirection = null
          }
          break
        case "Left":
          if (futureX <= this._tempDestination.x) {
            futureX = this._tempDestination.x
            this._tempDestination = null
            this._tempDirection = null
          }
          break
        case "Right":
          if (futureX >= this._tempDestination.x) {
            futureX = this._tempDestination.x
            this._tempDestination = null
            this._tempDirection = null
          }
          break
      }
    }

    return { x: futureX, y: futureY }
  }

  move() {
    if (this.level instanceof Match && !this.level.started) return
    if (!this.alive) return

    const { x: futureX, y: futureY } = this.getFutureCoords(this._tempDirection || this._direction, this.speed)

    let blocked = false

    const nextTiles = this._checkNextTiles({ x: futureX, y: futureY }, this._direction)
    if (nextTiles.front.some(({ blocked }) => blocked)) {
      this.level.getEntitiesAtTileCoords(nextTiles.front.map(tile => ({ x: tile.x, y: tile.y }))).forEach(entity => {
        if (entity !== this && entity instanceof Entity && this.checkCollision(entity, CollisionType.BoundingBox, { x: futureX, y: futureY })) {
          this.collide(entity, CollisionType.BoundingBox)
          if (entity.isBlocking(this.constructor)) {
            blocked = true
          }
        }
      })
    }

    // check for same position collisions
    this.level.children.forEach(entity => {
      if (entity !== this && entity instanceof Entity) {
        let collideHitBox = false
        let collideBoundingBox = false

        if (this.checkCollision(entity, CollisionType.HitBox, { x: this.x, y: this.y })) {
          collideHitBox = true
          collideBoundingBox = true // if the hitbox collides the bounding box will also collide
        }


        if (!collideBoundingBox && this.checkCollision(entity, CollisionType.BoundingBox, { x: this.x, y: this.y })) {
          collideBoundingBox = true
        }

        if (collideHitBox) {
          this.collide(entity, CollisionType.HitBox)
        }
        if (collideBoundingBox) {
          this.collide(entity, CollisionType.BoundingBox)
          if (entity.isBlocking(this)) {
            blocked = true
          }
        }
      }
    })


    // animate the sprite
    if (this._animations) {
      let nextMovementAnimation: Texture[] | null = null
      if (futureX < this.x) {
        nextMovementAnimation = this._animations["Left"]
      } else if (futureX > this.x) {
        nextMovementAnimation = this._animations["Right"]
      } else if (futureY < this.y) {
        nextMovementAnimation = this._animations["Up"]
      } else if (futureY > this.y) {
        nextMovementAnimation = this._animations["Down"]
      }

      if (nextMovementAnimation && nextMovementAnimation !== this._previousMovementAnimation) {
        this._previousMovementAnimation = nextMovementAnimation
        if (this._animationTimeout) {
          Clock.clear(this._animationTimeout)
          this._animationTimeout = null
        }

        // animation timeout to prevent the animation from changing too fast and causing a flicker
        this._animationTimeout = Clock.setTimeout(() => {
          const frame = this.currentFrame
          this.textures = nextMovementAnimation
          this.loop = true
          if (frame + 1 >= this.textures.length) {
            this.currentFrame = 0
          } else {
            this.currentFrame = frame + 1
          }
          this.play()
        }, 40)
      }
    }

    if (!blocked) {
      this.y = futureY
      this.x = futureX
      this.zIndex = this.y
    } else {
      switch (this._direction) {
        case "Up":
        case "Down":
          this.y = this.getTileCoords().y
          break
        case "Left":
        case "Right":
          this.x = this.getTileCoords().x
          break
      }

      let lowerDistance: number | null = null
      let momentumX: number = 0
      let momentumY: number = 0
      const momentum = Level.LEVEL_TILE_SIZE / 8

      if (nextTiles.side.every(tile => !tile.blocked)) {
        switch (this._previousDirection) {
          case "Up":
            momentumY -= momentum
            break
          case "Down":
            momentumY += momentum
            break
          case "Left":
            momentumX -= momentum
            break
          case "Right":
            momentumX += momentum
            break
        }
      }

      for (let i = 0; i < nextTiles.front.length; i++) {
        const tile = nextTiles.front[i]
        const distanceFromCenter = Math.abs(this.x - tile.x + momentumX) + Math.abs(this.y - tile.y + momentumY)
        let tileDistance = distanceFromCenter

        // penalize the distance from the center of the tile if the tile is blocked
        if (tile.blocked) {
          tileDistance += Level.LEVEL_TILE_SIZE / 2.5
        }

        if (lowerDistance === null) {
          lowerDistance = tileDistance
        }

        if (lowerDistance >= tileDistance) {
          this._tempDestination = { x: tile.x, y: tile.y }
          lowerDistance = tileDistance
        }
      }

      if (this._tempDestination) {

        if (SHOW_DEBUG_GRAPHICS && SHOW_BOUNDING_BOXES) {
          this.graphics.circle(this._tempDestination.x + Level.LEVEL_TILE_SIZE / 2, this._tempDestination.y + Level.LEVEL_TILE_SIZE / 2, 5)
          this.graphics.fill(0x00ffff)
        }

        switch (this._direction) {
          case "Up":
          case "Down": {
            if (this._tempDestination.x < this.x) {
              this._tempDirection = "Left"
            } else if (this._tempDestination.x > this.x) {
              this._tempDirection = "Right"
            } else {
              this._tempDirection = null
            }
            break
          }
          case "Left":
          case "Right": {
            if (this._tempDestination.y < this.y) {
              this._tempDirection = "Up"
            } else if (this._tempDestination.y > this.y) {
              this._tempDirection = "Down"
            } else {
              this._tempDirection = null
            }
            break
          }
        }
      }
    }
  }

  private _checkNextTiles(position: { x: number, y: number }, direction: Direction) {
    const { x, y } = this.getTileCoords(position)
    const nextTiles: {
      front: { x: number, y: number }[],
      side: { x: number, y: number }[]
    } = {
      front: [],
      side: []
    }

    switch (direction) {
      case "Up": {
        nextTiles.front.push(
          { x: x - Level.LEVEL_TILE_SIZE, y: y - Level.LEVEL_TILE_SIZE },
          { x: x, y: y - Level.LEVEL_TILE_SIZE },
          { x: x + Level.LEVEL_TILE_SIZE, y: y - Level.LEVEL_TILE_SIZE })
        nextTiles.side.push(
          { x: x - Level.LEVEL_TILE_SIZE, y: y },
          { x: x + Level.LEVEL_TILE_SIZE, y: y }
        )
        break
      }
      case "Down": {
        nextTiles.front.push(
          { x: x - Level.LEVEL_TILE_SIZE, y: y + Level.LEVEL_TILE_SIZE },
          { x: x, y: y + Level.LEVEL_TILE_SIZE },
          { x: x + Level.LEVEL_TILE_SIZE, y: y + Level.LEVEL_TILE_SIZE })
        nextTiles.side.push(
          { x: x - Level.LEVEL_TILE_SIZE, y: y },
          { x: x + Level.LEVEL_TILE_SIZE, y: y }
        )
        break
      }
      case "Left": {
        nextTiles.front.push(
          { x: x - Level.LEVEL_TILE_SIZE, y: y - Level.LEVEL_TILE_SIZE },
          { x: x - Level.LEVEL_TILE_SIZE, y: y },
          { x: x - Level.LEVEL_TILE_SIZE, y: y + Level.LEVEL_TILE_SIZE })
        nextTiles.side.push(
          { x: x, y: y - Level.LEVEL_TILE_SIZE },
          { x: x, y: y + Level.LEVEL_TILE_SIZE }
        )
        break
      }
      case "Right": {
        nextTiles.front.push(
          { x: x + Level.LEVEL_TILE_SIZE, y: y - Level.LEVEL_TILE_SIZE },
          { x: x + Level.LEVEL_TILE_SIZE, y: y },
          { x: x + Level.LEVEL_TILE_SIZE, y: y + Level.LEVEL_TILE_SIZE })
        nextTiles.side.push(
          { x: x, y: y - Level.LEVEL_TILE_SIZE },
          { x: x, y: y + Level.LEVEL_TILE_SIZE }
        )
        break
      }
    }

    return Object.entries(nextTiles).reduce<{ front: { x: number, y: number, blocked: boolean }[], side: { x: number, y: number, blocked: boolean }[] }>((acc, [key, value]) => {
      const nextTilesBlocked = value.map(position => {
        return this.level.isTileBlocked(position, this)
      })
      if (SHOW_DEBUG_GRAPHICS && SHOW_BOUNDING_BOXES) {
        for (let i = 0; i < value.length; i++) {
          this.graphics.rect(value[i].x, value[i].y, Level.LEVEL_TILE_SIZE, Level.LEVEL_TILE_SIZE)
          this.graphics.stroke({ width: 3, color: nextTilesBlocked[i] ? 0xff0000 : 0x00ff00 })
          this.graphics.circle(position.x, position.y, 5)
          this.graphics.fill(0xFF00FF)
        }
      }

      acc[key as keyof typeof nextTiles] = value.map((tile, i) => ({
        ...tile,
        blocked: nextTilesBlocked[i]
      }))
      return acc
    }, {
      front: [],
      side: []
    })
  }

  checkCollision(entity: Container | Entity, type: CollisionType, position?: { x: number, y: number }): boolean {
    if (entity instanceof Entity && entity !== this) {
      const { x: boxX, y: boxY, width, height } = type === CollisionType.BoundingBox ? this.getBoundingBox() : this.getHitBox()
      const boxXOffset = Math.abs(this.x - boxX)
      const boxYOffset = Math.abs(this.y - boxY)

      const { x, y } = { x: (position?.x || this.x) + boxXOffset, y: (position?.y || boxY) + boxYOffset }

      const thisBox = { x, y, width, height }
      const entityBox = type === CollisionType.BoundingBox ? entity.getBoundingBox() : entity.getHitBox()

      if (SHOW_DEBUG_GRAPHICS && SHOW_BOUNDING_BOXES) {
        this.graphics.rect(thisBox.x, thisBox.y, thisBox.width, thisBox.height)
        this.graphics.stroke({ width: 2, color: 0x0000FF })
      }

      return thisBox.x < entityBox.x + entityBox.width &&
        thisBox.x + thisBox.width > entityBox.x &&
        thisBox.y < entityBox.y + entityBox.height &&
        thisBox.y + thisBox.height > entityBox.y
    }

    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  collide(_entity: Container | Entity, _type: CollisionType) { }

  explode() {
    if (this.level instanceof Match && !this.level.started) return
    this.stopMoving()
    this._alive = false
  }

  isFacingEntity(entity: Entity): boolean {
    return this.isFacingPosition(entity.getTileCoords())
  }

  isFacingPosition(position: { x: number, y: number }): boolean {
    const { x: positionX, y: positionY } = position
    const { x: thisX, y: thisY } = this
    switch (this._direction) {
      case "Up":
        return thisX === positionX && thisY > positionY
      case "Down":
        return thisX === positionX && thisY < positionY
      case "Left":
        return thisY === positionY && thisX > positionX
      case "Right":
        return thisY === positionY && thisX < positionX
    }
  }

  isInTheSameTile(entity: Entity): boolean {
    const { x: entityX, y: entityY } = entity.getTileCoords()
    const { x: thisX, y: thisY } = this.getTileCoords()
    return thisX === entityX && thisY === entityY
  }
}
